import { memo, useContext, useMemo, type FunctionComponent } from 'react';
import size from 'lodash/size';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Typography from '@mui/material/Typography';
// Skillmore UI Components
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import CardsGrid from '@empathco/ui-components/src/components/CardsGrid';
// local imports
import { Leader } from '../graphql/types';
import { SupervisorContext } from '../context/supervisor';
import EmployeeCard, { EmployeeCardItem, EmployeeCardProps } from '../v3/EmployeeCard';
// SCSS imports
import { header, subheader } from './DelegatesList.module.scss';

type DelegatesListProps = {
  readonly hrbp?: boolean;
  readonly disabled?: boolean;
  readonly leader?: Leader | null;
}

const DelegatesList: FunctionComponent<DelegatesListProps> = ({
  hrbp = false,
  disabled: parentDisabled = false,
  leader
}) => {
  const {
    delegatesList: { data: delegates, pending, failed },
    delegateAdd: { pending: addDelegatePending },
    delegateRemove: { pending: removeDelegatePending, failed: removeDelegateFailed, params }, removeDelegate
  } = useContext(SupervisorContext);

  const { id: from_id, title } = leader || {};

  const disabled = !from_id || parentDisabled || addDelegatePending || removeDelegatePending ? true : undefined;

  const componentProps: Partial<EmployeeCardProps> = useMemo(() => ({
    actionLabel: 'delegates.button.remove',
    onAction: (to_id: number) => {
      if (from_id && to_id) removeDelegate?.({ from_id, to_id });
    }
  }), [from_id, removeDelegate]);

  return (
    <>
      {!pending && !failed && size(delegates) >= 1 ? (
        <CardSection top>
          <Typography className={header}>
            <FormattedMessage id="delegation.current_delegates"/>
          </Typography>
          {hrbp && title ? (
            <Typography className={subheader}>
              {title}
            </Typography>
          ) : undefined}
        </CardSection>
      ) : undefined}
      {leader ? (
        <CardsGrid
            variant="white"
            notFoundMessage={hrbp ? 'employees.hrbp.no_delegates' : 'employees.leader.no_delegates'}
            items={delegates as (EmployeeCardItem[] | null | undefined)}
            pending={pending}
            failed={failed}
            disabled={disabled}
            removingId={params?.to_id}
            component={EmployeeCard}
            ComponentProps={componentProps}
        />
      ) : undefined}
      <ActionSnackbar
          severity="error"
          message="delegation.remove_delegate_error"
          open={removeDelegateFailed}
      />
    </>
  );
};

export default memo(DelegatesList);
